import {
  Component,
  EventEmitter,
  Injectable,
  Input,
    inject,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Temporal } from '@js-temporal/polyfill';
import {
    CalendarEventTimesChangedEventType,
    CalendarUtils,
    CalendarWeekViewComponent,
    getWeekViewPeriod
} from 'angular-calendar';
import { DragEndEvent, DragMoveEvent } from 'angular-draggable-droppable';
import {
    CalendarEvent,
    EventColor,
    GetWeekViewArgs,
    WeekView,
    WeekViewAllDayEvent,
    WeekViewTimeEvent
} from 'calendar-utils';
import { EElimsAggregatedStatus } from 'src/app/domain/enums/elims-aggregate-status';
import { EventService } from 'src/app/services/event.service';
import { UserService } from 'src/app/services/user.service';
import { CoreData } from 'src/app/tools/core-data/core-data';
import { NotificationManager } from 'src/app/tools/ui/notification-manager';
import { BaseComponent } from '../base/base.component';
export interface DropEvent<T = any> {
    dropData: T;
    /**
     * ClientX value of the mouse location where the drop occurred
     */
    clientX: number;
    /**
     * ClientY value of the mouse location where the drop occurred
     */
    clientY: number;
    /**
     * The target of the event where the drop occurred
     */
    target: any;
}
export interface CalendarUser {
    id: number;
    name: string;
    Puid: string;
    color: EventColor;

    pumpWithBattery?: number;
    pumpWithoutBattery?: number;
    fan?: number;
    processMeasure?: number;

    dpumpWithBattery?: number;
    dpumpWithoutBattery?: number;
    dfan?: number;


    reducedMode: boolean;
}

interface DayViewScheduler extends WeekView {
    users: CalendarUser[];
}

interface GetWeekViewArgsWithUsers extends GetWeekViewArgs {
    users: CalendarUser[];
}

@Injectable()
export class DayViewSchedulerCalendarUtils extends CalendarUtils {


    override getWeekView(args: GetWeekViewArgsWithUsers): DayViewScheduler {
        const { period } = super.getWeekView(args);
        const view: DayViewScheduler = {
            period,
            allDayEventRows: [],
            hourColumns: [],
            users: [...args.users],
        };

        view.users.forEach((user, columnIndex) => {
            const events = args.events?.filter(
                (event) => event.meta.user.id === user.id
            );
            const columnView = super.getWeekView({
                ...args,
                events,
            });
            view.hourColumns.push(columnView.hourColumns[0]);
            columnView.allDayEventRows.forEach(({ row }, rowIndex) => {
                view.allDayEventRows[rowIndex] = view.allDayEventRows[rowIndex] || {
                    row: [],
                };
                view.allDayEventRows[rowIndex].row.push({
                    ...row[0],
                    offset: columnIndex,
                    span: 1,
                });
            });
        });

        return view;
    }
}

@Component({
    selector: 'mwl-day-view-scheduler',
    templateUrl: 'day-view-scheduler.component.html',
    providers: [DayViewSchedulerCalendarUtils],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DayViewSchedulerComponent
    extends CalendarWeekViewComponent
    implements OnChanges {
        EElimsAggregatedStatus = EElimsAggregatedStatus;
    private readonly schedulerUtils = inject(DayViewSchedulerCalendarUtils);
    @Input() users: CalendarUser[] = [];
    @Input() CalendarHeight: number = 0;

    @Output() userChanged = new EventEmitter();

    @Output() deleteClick = new EventEmitter();
    @Output() needRefresh = new EventEmitter();
    
    override view!: DayViewScheduler;

    override daysInWeek = 1;

    // get CalendarHeight() {
    //     return this.header ? `calc(100% - ${this.header.nativeElement.offsetHeight}px) !important` : 'auto !important';
    // }
    
    get CalendarWithoutHeaderHeight() {
        return this.users.some(x => x.reducedMode) ? this.CalendarHeight - 32 :  this.CalendarHeight - 115
    }

    constructor(
        private eventService: EventService,
        private userService: UserService,
        private coreData: CoreData,
        private snackBar: MatSnackBar
    ) {
        super();
    }

    trackByUserId = (index: number, row: CalendarUser) => row.id;

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['users']) {
            this.refreshBody();
            this.emitBeforeViewRender();
        }
    }

    override getDayColumnWidth(eventRowContainer: HTMLElement): number {
        return Math.floor(eventRowContainer.offsetWidth / this.users.length);
    }

    override dragMove(dayEvent: WeekViewTimeEvent, dragEvent: DragMoveEvent) {
        if (this.snapDraggedEvents) {
            const newUser = this.getDraggedUserColumn(dayEvent, dragEvent.x);
            const newEventTimes = this.getDragMovedEventTimes(
                dayEvent,
                { ...dragEvent, x: 0 },
                this.dayColumnWidth,
                true
            );
            const originalEvent = dayEvent.event;
            const adjustedEvent = {
                ...originalEvent,
                ...newEventTimes,
                meta: { ...originalEvent.meta, user: newUser },
            };
            const tempEvents = this.events.map((event) => {
                if (event === originalEvent) {
                    return adjustedEvent;
                }
                return event;
            });
            this.restoreOriginalEvents(
                tempEvents,
                new Map([[adjustedEvent, originalEvent]])
            );
        }
        this.dragAlreadyMoved = true;
    }

    override dragEnded(
        weekEvent: WeekViewAllDayEvent | WeekViewTimeEvent,
        dragEndEvent: DragEndEvent,
        dayWidth: number,
        useY = false
    ) {
        super.dragEnded(
            weekEvent,
            {
                ...dragEndEvent,
                x: 0,
            },
            dayWidth,
            useY
        );
        const newUser = this.getDraggedUserColumn(weekEvent, dragEndEvent.x);
        if (newUser) { // newUser !== weekEvent.event.meta.user
            this.userChanged.emit({ event: weekEvent.event, newUser });
        }
    }

    protected override getWeekView(events: CalendarEvent[]) {
        return this.schedulerUtils.getWeekView({
            events,
            users: this.users,
            viewDate: this.viewDate,
            weekStartsOn: this.weekStartsOn,
            excluded: this.excludeDays,
            precision: this.precision,
            absolutePositionedEvents: true,
            hourSegments: this.hourSegments,
            dayStart: {
                hour: this.dayStartHour,
                minute: this.dayStartMinute,
            },
            dayEnd: {
                hour: this.dayEndHour,
                minute: this.dayEndMinute,
            },
            segmentHeight: this.hourSegmentHeight,
            weekendDays: this.weekendDays,
            ...getWeekViewPeriod(
                this.dateAdapter,
                this.viewDate,
                this.weekStartsOn,
                this.excludeDays,
                this.daysInWeek
            ),
        });
    }

    private getDraggedUserColumn(
        dayEvent: WeekViewTimeEvent | WeekViewAllDayEvent,
        xPixels: number
    ) {
        const columnsMoved = Math.round(xPixels / this.dayColumnWidth);
        const currentColumnIndex = this.view.users.findIndex(
            (user) => user === dayEvent.event.meta.user
        );
        const newIndex = currentColumnIndex + columnsMoved;
        return this.view.users[newIndex];
    }

    onDeleteClicked(event: Event, weekEvent: CalendarEvent) {
        event.stopPropagation();
        if (Temporal.PlainDate.compare(weekEvent.meta.event.From, Temporal.Now.plainDateISO()) === -1) {
            NotificationManager.handleError('Impossible de supprimer un événement dans le passé !', this.snackBar)
            return;
        }
        this.deleteClick.emit({ code: weekEvent.meta.event.Code });
    }

    override eventDropped(dropEvent: DropEvent<{ event?: CalendarEvent; calendarId?: symbol; }>, date: Date, allDay: boolean): void {

        if (!dropEvent.dropData.event?.meta.isExt)
            super.eventDropped(dropEvent, date, allDay);
        else {
            // Left spacing + Time label column
            dropEvent.dropData.event.meta.user = this.view.users[Math.floor((dropEvent.clientX - 80) / dropEvent.target.offsetWidth)];
            this.eventTimesChanged.emit({
                type: CalendarEventTimesChangedEventType.Drop,
                event: dropEvent.dropData.event!,
                newStart: date,
                newEnd: new Date(date.getTime() + dropEvent.dropData.event.meta.duration * (1000 * 60)),
                allDay,
            });
        }
    }

    cleanSamplerDay(user: CalendarUser) {
        this.eventService.deleteAllEventsForASampler(this.coreData.Agency?.AnalyticalUnitCode!, this.viewDate.toISOString(), user.Puid).subscribe(x => {
            this.needRefresh.emit({ user });
        });
    }

    sendMail(user: CalendarUser) {
        this.userService.sendConfirmationMailToSampler(this.coreData.Agency?.AnalyticalUnitCode!, this.viewDate.toISOString(), user.Puid).subscribe(x => {
            if (x) {
                NotificationManager.handleSuccess('Email envoyé !', this.snackBar)
            }
        });
    }

    get IsDispatch() {
        return this.coreData.RoleAndPermissions.Name === 'Dispatch';
    }

    get IsValideur() {
        return this.coreData.RoleAndPermissions.Name === 'Valideur';
    }

    get IsTechnicien() {
        return this.coreData.RoleAndPermissions.Name === 'Preleveur';
    }

    detectMargin() {
        return this.IsDispatch ? '18' : '0';
    }
}
